import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PageHero from '../../components/ui/PageHero';
import PageCTA from '../../components/ui/PageCTA';
import Section from '../../components/ui/Section';
import SectionHeader from '../../components/ui/SectionHeader';
import BrandIcon from '../../components/ui/BrandIcon';
import {
  lmsOverview,
  lmsLiveDemoUrl,
  lmsStudentFeatures,
  lmsAdminFeatures,
  lmsTechnicalFeatures,
} from '../../data/lmsPlatform';

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 800px;
  margin: 0 auto 48px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 20px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e8eef3;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 1.75rem;
    font-weight: 800;
    color: #0e6b3c;
    line-height: 1;
  }

  span {
    font-size: 0.78rem;
    color: #64748b;
    margin-top: 6px;
    display: block;
  }
`;

const TechPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 40px;

  span {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 100px;
    background: rgba(14, 107, 60, 0.08);
    border: 1px solid rgba(14, 107, 60, 0.15);
    color: #0e6b3c;
  }
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 36px;
`;

const TabBtn = styled.button`
  padding: 10px 22px;
  border-radius: 100px;
  border: 1px solid ${(p) => (p.$active ? 'transparent' : '#e2e8f0')};
  background: ${(p) => (p.$active ? '#0e6b3c' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#64748b')};
  font-weight: 700;
  font-size: 0.85rem;
  font-family: var(--font-display);
  cursor: pointer;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  padding: 22px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eef3;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(14, 107, 60, 0.1);
  }

  h3 {
    font-family: var(--font-display);
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    margin: 14px 0 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  ul {
    margin: 0;
    padding: 0 0 0 18px;
    list-style: disc;

    li {
      font-size: 0.8rem;
      color: #64748b;
      line-height: 1.55;
      margin-bottom: 4px;
    }
  }

  p.desc {
    font-size: 0.82rem;
    color: #64748b;
    line-height: 1.6;
    margin: 10px 0 0;
  }
`;

const MobileBanner = styled.div`
  margin-top: 48px;
  padding: 28px 32px;
  border-radius: 20px;
  background: linear-gradient(135deg, #0e6b3c, #1e3a5f);
  color: #fff;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  h3 {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 800;
    margin: 0 0 8px;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.9;
    max-width: 520px;
    line-height: 1.6;
  }

  .icons {
    display: flex;
    gap: 16px;
    font-size: 2rem;
    opacity: 0.95;
  }
`;

const TopActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0e6b3c;
  text-decoration: none;

  &:hover {
    color: #6dbb1a;
  }
`;

const LiveDemoBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 100px;
  background: #0e6b3c;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: var(--font-display);
  text-decoration: none;
  box-shadow: 0 6px 20px rgba(14, 107, 60, 0.28);

  &:hover {
    background: #0a5a32;
  }
`;

const TAB_STUDENT = 'student';
const TAB_ADMIN = 'admin';
const TAB_TECH = 'technical';

const LmsPlatform = () => {
  const [tab, setTab] = useState(TAB_STUDENT);

  const featureSets = {
    [TAB_STUDENT]: lmsStudentFeatures,
    [TAB_ADMIN]: lmsAdminFeatures,
    [TAB_TECH]: lmsTechnicalFeatures,
  };

  const activeFeatures = featureSets[tab];

  return (
    <div>
      <PageHero
        label="Our Product"
        title="Education LMS"
        titleHighlight="Platform"
        description={lmsOverview.summary}
        breadcrumb="LMS Platform"
      />

      <Section>
        <div className="container">
          <TopActions>
            <BackLink to="/projects">
              <i className="fas fa-arrow-left" /> Back to portfolio
            </BackLink>
            <LiveDemoBtn href={lmsLiveDemoUrl} target="_blank" rel="noopener noreferrer">
              Visit live demo <i className="fas fa-external-link-alt" />
            </LiveDemoBtn>
          </TopActions>

          <StatsRow>
            {lmsOverview.stats.map((s) => (
              <StatCard key={s.label}>
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </StatCard>
            ))}
          </StatsRow>

          <TechPills>
            {lmsOverview.techStack.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </TechPills>

          <SectionHeader
            label="Capabilities"
            title="Everything"
            titleHighlight="Included"
            description="Student portal, admin panel, mobile app, payments, and AI — one unified platform."
            align="center"
          />

          <Tabs>
            <TabBtn type="button" $active={tab === TAB_STUDENT} onClick={() => setTab(TAB_STUDENT)}>
              Student Features
            </TabBtn>
            <TabBtn type="button" $active={tab === TAB_ADMIN} onClick={() => setTab(TAB_ADMIN)}>
              Admin Features
            </TabBtn>
            <TabBtn type="button" $active={tab === TAB_TECH} onClick={() => setTab(TAB_TECH)}>
              Technical
            </TabBtn>
          </Tabs>

          <FeatureGrid
            as={motion.div}
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeFeatures.map((f) => (
              <FeatureCard key={f.title}>
                <BrandIcon $size={40}>
                  <i className={f.icon} />
                </BrandIcon>
                <h3>{f.title}</h3>
                {f.items ? (
                  <ul>
                    {f.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="desc">{f.description}</p>
                )}
              </FeatureCard>
            ))}
          </FeatureGrid>

          <MobileBanner>
            <div>
              <h3>Web + Mobile App</h3>
              <p>
                Native-style mobile experience with Firebase push notifications, synced with the
                same REST API as the web dashboard. Admins configure app keys from the settings
                panel.
              </p>
            </div>
            <div className="icons" aria-hidden>
              <i className="fas fa-laptop" />
              <i className="fas fa-tablet-alt" />
              <i className="fas fa-mobile-alt" />
            </div>
          </MobileBanner>
        </div>
      </Section>

      <PageCTA
        title="Want This LMS For Your Institute?"
        description="We customize branding, exams, payments, and AI providers for your organisation."
        primaryLabel="Request a Demo"
        primaryTo="/contact"
        secondaryLabel="View All Projects"
        secondaryTo="/projects"
      />
    </div>
  );
};

export default LmsPlatform;
